/* eslint-disable */
import Bugsnag from '@bugsnag/browser';
import BugsnagPerformance from '@bugsnag/browser-performance';
import { VueRouterRoutingProvider } from '@bugsnag/vue-router-performance';

/**
 * Bugsnag Error Tracking & Performance Monitoring Module
 * Handles error reporting and performance tracking for the browser
 */

// Vue Router integration for BugsnagPerformance
let vueRouterInstance = null;
let bugsnagErrorsInitialized = false;
let bugsnagPerformanceInitialized = false;

// Per (group + day) count for rate limiting on client
const errorGroupDailyCounts = new Map();
const ERROR_GROUP_DATE_STR_LEN = 10; // YYYY-MM-DD

function getErrorGroupKey(event) {
  const e = event.errors && event.errors[0];
  if (!e) return 'unknown';
  const frame = e.stacktrace && e.stacktrace[0];
  const loc = frame ? (frame.file || '') + ':' + (frame.lineNumber ?? '') : '';
  return (e.errorClass || 'Error') + '\n' + (e.errorMessage || '') + '\n' + loc;
}

function shouldDiscardByDailyLimit(event, limit) {
  const groupKey = getErrorGroupKey(event);
  const today = new Date().toISOString().slice(0, ERROR_GROUP_DATE_STR_LEN);
  const key = `${today}:${groupKey}`;
  let entry = errorGroupDailyCounts.get(key);
  if (!entry) {
    entry = { count: 0 };
    errorGroupDailyCounts.set(key, entry);
  }
  entry.count += 1;
  return entry.count > limit;
}

function verboseLog(msg, data) {
  if (typeof window !== 'undefined' && window.GT && window.GT.bugsnag && window.GT.bugsnag.client && window.GT.bugsnag.client.verbose) {
    console.log('[Bugsnag] ' + msg, data !== undefined ? data : '');
  }
}

// Returns span duration in ms, or null if unknown. Handles OTel HrTime [sec, nsec], number (ms), and our custom perf.duration_sec attribute.
function getSpanDurationMs(span) {
  if (!span) return null;
  let secFromAttr = span.attributes?.['perf.duration_sec'];
  if (secFromAttr == null && span.attributes?.get) secFromAttr = span.attributes.get('perf.duration_sec');
  if (secFromAttr == null && typeof span.getAttribute === 'function') secFromAttr = span.getAttribute('perf.duration_sec');
  if (secFromAttr == null && Array.isArray(span.attributes)) {
    const entry = span.attributes.find(a => a && (a.key === 'perf.duration_sec' || a.name === 'perf.duration_sec'));
    if (entry && entry.value != null) secFromAttr = entry.value.doubleValue ?? entry.value.intValue ?? entry.value;
  }
  if (typeof secFromAttr === 'number' && secFromAttr >= 0) return Math.round(secFromAttr * 1000);
  const toMs = (t) => {
    if (t == null) return null;
    if (typeof t === 'number') return t;
    if (Array.isArray(t) && t.length >= 2) return t[0] * 1000 + t[1] / 1e6;
    return null;
  };
  if (span.duration != null) {
    const d = toMs(span.duration);
    if (d != null && d >= 0) return d;
  }
  const start = span.startTime;
  const end = span.endTime;
  if (start == null || end == null) return null;
  const startMs = toMs(start);
  const endMs = toMs(end);
  if (startMs == null || endMs == null) return null;
  const durationMs = endMs - startMs;
  if (durationMs < 0) return null;
  return durationMs;
}

/**
 * Default settings for BugsnagPerformance browser instrumentation
 */
const BUGSNAG_PERFORMANCE_DEFAULTS = {
  autoInstrumentFullPageLoads: true,
  autoInstrumentNetworkRequests: true,
  autoInstrumentRouteChanges: true,
  generateAnonymousId: false,
  maximumBatchSize: 100,
};

/**
 * Set Vue Router instance for performance tracking
 */
export function setRouter(router) {
  vueRouterInstance = router;
}

/**
 * Initialize Bugsnag error reporting and performance monitoring
 */
export function initBugsnag(debug = false) {
  try {
    const GT = typeof window !== 'undefined' ? window.GT : undefined;
    // Initialize Bugsnag Errors (config from server; no need for window.Bugsnag before start)
    if (GT && GT.bugsnag && GT.bugsnag.client && GT.bugsnag.client.bugsnagApiKey &&
        GT.bugsnag.client.errors && GT.bugsnag.client.errors.enabled) {
      const bugSnagClientConfig = {
        apiKey: GT.bugsnag.client.bugsnagApiKey,
        releaseStage: GT.env,
        autoTrackSessions: false,
        sendCode: false,
        appVersion: GT.buildVersion,
        verbose: Boolean(GT.bugsnag.client.verbose),
        autoDetectErrors: true,
        enabledErrorTypes: { unhandledExceptions: true, unhandledRejections: true },
      };
      if (GT.bugsnag.client.enabledReleaseStages && GT.bugsnag.client.enabledReleaseStages.length) {
        bugSnagClientConfig.enabledReleaseStages = GT.bugsnag.client.enabledReleaseStages;
      }
      const maxPerGroupPerDay = GT.bugsnag.client.errors.maxEventsPerGroupPerDay;
      const verboseErrors = Boolean(GT.bugsnag.client.verbose);
      bugSnagClientConfig.onError = (event) => {
        if (typeof maxPerGroupPerDay === 'number' && maxPerGroupPerDay > 0 && shouldDiscardByDailyLimit(event, maxPerGroupPerDay)) {
          return false;
        }
        if (verboseErrors) {
          const e = event.errors && event.errors[0];
          const name = e ? (e.errorClass || 'Error') : '?';
          const msg = e ? (e.errorMessage || '') : '?';
          console.log('[Bugsnag] error sent | ' + name + ' | ' + msg);
        }
        return true;
      };

      Bugsnag.start(bugSnagClientConfig);
      bugsnagErrorsInitialized = true;
      verboseLog('errors initialized');

      if (debug && window.GT) {
        Bugsnag.addMetadata(window.GT);
      }
    }

    // Set Bugsnag user info and metadata
    if (bugsnagErrorsInitialized && window.user && window.user.id) {
      let userName = window.user.email;
      if (window.user.firstName) {
        userName = window.user.firstName + (window.user.lastName ? ' ' + window.user.lastName : '');
      }

      Bugsnag.setUser(
        String(window.user.id),
        window.user.email,
        userName
      );

      // User metadata
      Bugsnag.addMetadata('user', {
        userId: window.user.id,
        email: window.user.email,
        locale: window.user.locale,
        loginMethod: window.user.loginMethod,
        registrationTime: window.user.registrationTime,
      });

      // Team metadata
      if (window.user.team) {
        Bugsnag.addMetadata('team', {
          teamId: window.user.team.id,
          isOwner: window.user.team.is_owner,
          ownerId: window.user.team.owner_id,
          ownerEmail: window.user.team.owner_email,
          isJira: window.user.team.is_jira,
        });
      }

      // Subscription metadata
      if (window.user.subscription) {
        Bugsnag.addMetadata('subscription', {
          isTrial: window.user.subscription.is_trial,
          isAnnual: window.user.subscription.is_annual,
          planId: window.user.subscription.payment_plans_id,
          provider: window.user.subscription.provider,
          userCount: window.user.subscription.user_count,
          dateTo: window.user.subscription.date_to,
        });
      }

      // SSO metadata
      if (window.user.sso) {
        Bugsnag.addMetadata('sso', {
          enabled: window.user.sso.enabled,
        });
      }

      // 2FA metadata
      if (window.user.twoFa) {
        Bugsnag.addMetadata('twoFa', {
          active: window.user.twoFa.active,
        });
      }
    }

    // Bugsnag automatically collects: host, pathname, userAgent, screen size, etc.
    // appVersion and releaseStage are already set in config above

    // Initialize BugsnagPerformance
    const perfEnabled = GT && GT.bugsnag && GT.bugsnag.client && GT.bugsnag.client.performance && GT.bugsnag.client.performance.enabled;
    if (perfEnabled) {
      const perfConfig = GT.bugsnag.client.performance;
      const minAutomaticMs = perfConfig.minAutomaticRequestDurationMs ?? 3000;
      const minCustomMs = perfConfig.minCustomSpanDurationMs ?? 1000;

      const samplingProbability = perfConfig.samplingProbability != null
        ? perfConfig.samplingProbability
        : (GT.bugsnag.client.verbose ? 1.0 : undefined);
      const verbosePerf = Boolean(GT.bugsnag.client.verbose);
      const performanceConfig = {
        apiKey: GT.bugsnag.client.bugsnagApiKey,
        appVersion: GT.buildVersion,
        releaseStage: GT.env,
        bugsnag: Bugsnag, // Link Errors SDK: user (setUser) and errors are associated with performance traces in dashboard
        autoInstrumentFullPageLoads: BUGSNAG_PERFORMANCE_DEFAULTS.autoInstrumentFullPageLoads,
        autoInstrumentNetworkRequests: BUGSNAG_PERFORMANCE_DEFAULTS.autoInstrumentNetworkRequests,
        autoInstrumentRouteChanges: BUGSNAG_PERFORMANCE_DEFAULTS.autoInstrumentRouteChanges,
        generateAnonymousId: BUGSNAG_PERFORMANCE_DEFAULTS.generateAnonymousId,
        maximumBatchSize: (verbosePerf || samplingProbability === 1) ? 10 : (perfConfig.maximumBatchSize ?? BUGSNAG_PERFORMANCE_DEFAULTS.maximumBatchSize),
      };
      if (verbosePerf) {
        performanceConfig.logger = {
          debug: (msg) => console.log('[Bugsnag Perf]', msg),
          info: (msg) => console.log('[Bugsnag Perf]', msg),
          warn: (msg) => console.warn('[Bugsnag Perf]', msg),
          error: (msg) => console.error('[Bugsnag Perf]', msg),
        };
      }
      if (samplingProbability != null) {
        performanceConfig.samplingProbability = samplingProbability;
      }

      const onSpanEndFn = (span) => {
        try {
          const durationMs = getSpanDurationMs(span);
          const name = (span && (span.name || (span.spanContext && span.spanContext.name))) || 'span';
          const isCustomSpan = typeof name === 'string' && name.length > 0 && name[0] !== '[';
          if (isCustomSpan) {
            const sendCustom = durationMs === null || durationMs >= minCustomMs;
            if (verbosePerf) {
              const action = sendCustom ? 'sent' : 'dropped (< ' + minCustomMs + 'ms)';
              const dur = durationMs != null ? ' | durationMs=' + durationMs : '';
              console.log('[Bugsnag] perf ' + action + ' | ' + name + dur);
            }
            if (durationMs !== null && durationMs < minCustomMs) return false;
            return true;
          }
          const send = durationMs === null || durationMs >= minAutomaticMs;
          if (durationMs !== null && durationMs < minAutomaticMs) {
            return false;
          }
          return true;
        } catch (e) {
          console.error('[Bugsnag] onSpanEnd error:', e);
          return true;
        }
      };
      performanceConfig.onSpanEnd = [onSpanEndFn];

      // Add Vue Router integration if available
      if (vueRouterInstance) {
        performanceConfig.routingProvider = new VueRouterRoutingProvider(vueRouterInstance);
      }

      verboseLog('performance: batch size ' + performanceConfig.maximumBatchSize + ' (smaller = send sooner); watch for [Bugsnag Perf] logs and Network requests to bugsnag.com');

      BugsnagPerformance.start(performanceConfig);
      bugsnagPerformanceInitialized = true;
      verboseLog('performance initialized | auto>=' + minAutomaticMs + 'ms custom>=' + minCustomMs + 'ms | samplingProbability=' + (samplingProbability != null ? samplingProbability : 'default'));
      verboseLog('performance: check Network tab for requests to bugsnag.com; ensure dashboard release stage includes this env');
      if (typeof window !== 'undefined' && GT.bugsnag.client.verbose) {
        window.testBugsnagPerfSpan = testPerfSpan;
      }

      if (typeof window !== 'undefined') {
        const flushPerf = () => {
          if (typeof BugsnagPerformance.flush === 'function') {
            BugsnagPerformance.flush();
            if (verbosePerf) console.log('[Bugsnag] perf flush called');
          }
        };
        window.addEventListener('pagehide', flushPerf);
        window.addEventListener('beforeunload', flushPerf);
        if (verbosePerf && typeof BugsnagPerformance.flush === 'function') {
          setInterval(flushPerf, 15000);
        }
      }
    }

  } catch (error) {
    console.error('[Bugsnag] Initialization failed:', error);
  }
}

export function isBugsnagInitialized() {
  return bugsnagErrorsInitialized;
}

export function isBugsnagPerformanceInitialized() {
  return bugsnagPerformanceInitialized;
}

// Custom spans: https://docs.bugsnag.com/performance/integration-guides/web/#sending-custom-spans
export function startPerfSpan(name) {
  if (!bugsnagPerformanceInitialized) return null;
  try {
    const span = BugsnagPerformance.startSpan(name);
    verboseLog('perf span started | ' + name);
    return span;
  } catch (e) {
    return null;
  }
}

export function verboseLogPerfSpanEnd(name, durationMs) {
  verboseLog('perf span ended | ' + name + ' | durationMs=' + durationMs);
}

// Test helper: creates a custom span "testCustomSpan" lasting ~1.5s. Call from console: window.testBugsnagPerfSpan() or import and testPerfSpan().
export function testPerfSpan(durationSec = 1.5) {
  if (!bugsnagPerformanceInitialized) {
    console.warn('[Bugsnag] testPerfSpan: Performance not initialized');
    return;
  }
  const name = 'testCustomSpan';
  const span = startPerfSpan(name);
  if (!span) return;
  setTimeout(() => {
    const sec = durationSec;
    if (span.setAttribute) span.setAttribute('perf.duration_sec', sec);
    verboseLogPerfSpanEnd(name, Math.round(sec * 1000));
    if (span.end) span.end();
    console.log('[Bugsnag] test span ended, durationMs=' + Math.round(sec * 1000));
  }, durationSec * 1000);
}

// Safe notify: preserves original stack, does not throw. Used by log.error().
export function notifyErrorSafely(error) {
  const err = error instanceof Error ? error : new Error(String(error));

  if (!bugsnagErrorsInitialized && typeof window !== 'undefined') {
    const GT = window.GT;
    if (GT && GT.bugsnag && GT.bugsnag.client && GT.bugsnag.client.bugsnagApiKey &&
        GT.bugsnag.client.errors && GT.bugsnag.client.errors.enabled) {
      const lazyConfig = {
        apiKey: GT.bugsnag.client.bugsnagApiKey,
        releaseStage: GT.env,
        autoTrackSessions: false,
        sendCode: false,
        appVersion: GT.buildVersion,
        verbose: Boolean(GT.bugsnag.client.verbose),
        autoDetectErrors: true,
        enabledErrorTypes: { unhandledExceptions: true, unhandledRejections: true },
      };
      if (GT.bugsnag.client.enabledReleaseStages && GT.bugsnag.client.enabledReleaseStages.length) {
        lazyConfig.enabledReleaseStages = GT.bugsnag.client.enabledReleaseStages;
      }
      Bugsnag.start(lazyConfig);
      bugsnagErrorsInitialized = true;
    }
  }

  if (!bugsnagErrorsInitialized) return;

  verboseLog('reporting error', { message: err.message, workerSource: err.workerSource || null });

  try {
    Bugsnag.notify(err, (event) => {
      if (err.workerSource) {
        event.addMetadata('worker', { source: err.workerSource });
      }
    });
  } catch (e) {
    console.error('[Bugsnag] notify failed:', e);
  }
}
